/**
 * Language to send to the TTS service with each text chunk.
 *
 * The TTS service picks its model from this code (English model for "en",
 * Chatterbox Multilingual for the others). Japanese / Chinese / Korean / Thai …
 * are also detected on the server from the script, but French, German, Spanish,
 * Portuguese, Malay and Indonesian are written in Latin letters like English, so
 * the server can't tell them apart — the UI has to send the code.
 *
 * Courses don't store which language they were written in, and a course created
 * in English stays English after the user switches the site to French. So the
 * code is decided from the text itself:
 *   - non-Latin script            -> that language
 *   - text that reads as English  -> "en"
 *   - otherwise                   -> the current content language
 */
import { getContentLanguage } from "./contentLanguage";

const ENGLISH_WORDS = new Set([
  "the", "and", "to", "of", "a", "in", "is", "you", "your", "for", "that", "it", "with",
  "on", "this", "are", "as", "be", "or", "can", "will", "how", "what", "we", "our", "by",
  "an", "from", "at", "they", "their", "have", "has", "more", "not", "but", "about", "which",
  "these", "into", "when", "who", "why", "do", "does", "its", "was", "were", "there", "than",
]);

const scriptLanguage = (text: string): string | null => {
  if (/[぀-ヿ]/.test(text)) return "ja";
  if (/[一-鿿]/.test(text)) {
    // Kanji-only chunks (no kana) are still Japanese when the content language is Japanese
    const lang = getContentLanguage();
    if (lang === "ja") return "ja";
    return lang === "zh-TW" || lang === "zh-CN" ? lang : "zh-CN";
  }
  if (/[가-힯]/.test(text)) return "ko";
  if (/[฀-๿]/.test(text)) return "th";
  return null;
};

export const looksEnglish = (text: string): boolean => {
  const words = (text.toLowerCase().match(/[a-zà-ÿ']+/g) || []);
  if (words.length < 5) {
    // too short to judge: accented letters mean "not English"
    return !/[à-ÿœß]/i.test(text);
  }
  const hits = words.filter((w) => ENGLISH_WORDS.has(w)).length;
  return hits / words.length >= 0.12;
};

export const ttsLanguageFor = (text: string): string => {
  const script = scriptLanguage(text || "");
  if (script) return script;
  if (looksEnglish(text || "")) return "en";
  const lang = getContentLanguage();
  return lang || "en";
};
